import { logHttpRequestApi } from '@core/core/api'
import type { HttpRequestLog } from '@core/core/types'
import { isHttpRequestType } from '@core/core/types'
import { v4 as generateUUIDv4 } from 'uuid'
import xspy from 'xspy'
import type {
  ListenerForRequest,
  ListenerForResponse,
  // eslint-disable-next-line import/no-internal-modules
} from 'xspy/dist/types/index.type'

const textEncoder = new TextEncoder()

const getFormDataInBytes = (formData: FormData) => {
  let totalSizeInBytes = 0
  for (const value of formData.values()) {
    totalSizeInBytes += value instanceof Blob ? value.size : textEncoder.encode(value).byteLength
  }
  return totalSizeInBytes
}

const getBodySizeInBytes = (body: BodyInit | Document | null | undefined) => {
  if (body instanceof FormData) {
    return getFormDataInBytes(body)
  }

  if (body instanceof Blob) {
    return body.size
  }

  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
    return body.byteLength
  }

  if (typeof body === 'string') {
    return textEncoder.encode(body).byteLength
  }

  return 0
}

const isSameDomain = (url: string) => url.startsWith('/') || url.startsWith(window.location.origin)

const isFrontendLoggingUrl = (url: string) => url.includes('FrontendLogging')

const isExceptedFromTelemetry = (url: string) => !isSameDomain(url) || isFrontendLoggingUrl(url)

const requestListener: ListenerForRequest<'xhr' | 'fetch'> = request => {
  if (isExceptedFromTelemetry(request.url)) {
    return
  }

  request.headers['X-Correlation-Id'] = generateUUIDv4()
}

const responseListener: ListenerForResponse<'xhr' | 'fetch'> = (request, response) => {
  if (isExceptedFromTelemetry(request.url)) {
    return
  }

  logHttpRequestApi({
    httpRequestType: isHttpRequestType(request.method) ? request.method : 'GET',
    url: request.url,
    sizeInBytes: getBodySizeInBytes(request.body),
    date: new Date(Date.now()).toISOString(),
    status: response.status,
    correlationId: request.headers['X-Correlation-Id'],
  } satisfies HttpRequestLog)
}

let isEnabled = false

export const loggingNetworkListeners = {
  enable() {
    if (isEnabled) {
      return
    }
    xspy.onRequest(requestListener)
    xspy.onResponse(responseListener)
    isEnabled = true
  },
  disable() {
    xspy.offRequest(requestListener)
    xspy.offResponse(responseListener)
  },
}
